// Grouped client for AppOtg operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { DataFormActionResponse } from '../models/DataFormActionResponse';
import { Settings } from '../models/Settings';
import { UpdateSettingsRequest } from '../models/UpdateSettingsRequest';
import { CreateSettingsRequest } from '../models/CreateSettingsRequest';
import { FilterSettingsRequest } from '../models/FilterSettingsRequest';
import { DataviewSettingsResponse } from '../models/DataviewSettingsResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { DataFormActionResponseSerializer } from '../serializers/DataFormActionResponseSerializer';
import { SettingsSerializer } from '../serializers/SettingsSerializer';
import { UpdateSettingsRequestSerializer } from '../serializers/UpdateSettingsRequestSerializer';
import { CreateSettingsRequestSerializer } from '../serializers/CreateSettingsRequestSerializer';
import { FilterSettingsRequestSerializer } from '../serializers/FilterSettingsRequestSerializer';
import { DataviewSettingsResponseSerializer } from '../serializers/DataviewSettingsResponseSerializer';

/**
 * HTTP client for AppOtg operations
 *
 * Client for AppOtg operations
 */
export class AppOtgClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForSettings(formName: string, objectId: string, clientId?: string | null, entityId?: string | null, name?: string | null, description?: string | null, id?: string | null, createdOn?: string | null, lastModifiedOn?: string | null, directionsProvider?: string | null, phoneCallProvider?: string | null, smsProvider?: string | null, theme?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/app.otg.Settings({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (clientId !== undefined) {
      queryParams.append('ClientId', String(clientId));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (lastModifiedOn !== undefined) {
      queryParams.append('LastModifiedOn', String(lastModifiedOn));
    }
    if (directionsProvider !== undefined) {
      queryParams.append('DirectionsProvider', String(directionsProvider));
    }
    if (phoneCallProvider !== undefined) {
      queryParams.append('PhoneCallProvider', String(phoneCallProvider));
    }
    if (smsProvider !== undefined) {
      queryParams.append('SMSProvider', String(smsProvider));
    }
    if (theme !== undefined) {
      queryParams.append('Theme', String(theme));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeSettings(formName: string, clientId?: string | null, entityId?: string | null, name?: string | null, description?: string | null, id?: string | null, createdOn?: string | null, lastModifiedOn?: string | null, directionsProvider?: string | null, phoneCallProvider?: string | null, smsProvider?: string | null, theme?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/app.otg.Settings/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (clientId !== undefined) {
      queryParams.append('ClientId', String(clientId));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (lastModifiedOn !== undefined) {
      queryParams.append('LastModifiedOn', String(lastModifiedOn));
    }
    if (directionsProvider !== undefined) {
      queryParams.append('DirectionsProvider', String(directionsProvider));
    }
    if (phoneCallProvider !== undefined) {
      queryParams.append('PhoneCallProvider', String(phoneCallProvider));
    }
    if (smsProvider !== undefined) {
      queryParams.append('SMSProvider', String(smsProvider));
    }
    if (theme !== undefined) {
      queryParams.append('Theme', String(theme));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getUpsertFormForTypeSettings(entityId?: string | null, clientId?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    const path = '/app/api/Object/app.otg.Settings/Upsert/DataForm';

    const queryParams = new URLSearchParams();
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (clientId !== undefined) {
      queryParams.append('ClientId', String(clientId));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterSettings(request?: FilterSettingsRequest | null, options?: { headers?: Record<string, string> }): Promise<Settings[]> {
    const path = '/app/api/Object/app.otg.Settings/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterSettingsRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => SettingsSerializer.fromObject(item));
  }

  async createSettings(request?: CreateSettingsRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    const path = '/app/api/Object/app.otg.Settings';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(CreateSettingsRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async updateSettings(objectId: string, request?: UpdateSettingsRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/app.otg.Settings({objectId})'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'PATCH',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(UpdateSettingsRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async recentSettings(request?: FilterSettingsRequest | null, options?: { headers?: Record<string, string> }): Promise<Settings[]> {
    const path = '/app/api/Object/app.otg.Settings/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterSettingsRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => SettingsSerializer.fromObject(item));
  }

  async dataViewSettings(request?: FilterSettingsRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewSettingsResponse> {
    const path = '/app/api/Object/app.otg.Settings/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterSettingsRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewSettingsResponseSerializer.fromObject(data);
  }

}
